import { createContext, useContext, useState, ReactNode } from 'react'
import { CartItem, Package } from '../types'

interface CartContextType {
  cartItems: CartItem[]
  addToCart: (pkg: Package) => void
  removeFromCart: (packageId: string) => void
  updateQuantity: (packageId: string, quantity: number) => void
  clearCart: () => void
  getTotalPrice: () => number
  getTotalItems: () => number
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([])

  const addToCart = (pkg: Package) => {
    setCartItems((prevItems) => {
      // Sepette zaten bir ürün varsa, önceki ürünü kaldır ve yeni ürünü ekle
      if (prevItems.length > 0) {
        return [{ ...pkg, quantity: 1 }]
      }
      // Sepet boşsa, ürünü ekle
      return [{ ...pkg, quantity: 1 }]
    })
  }

  const removeFromCart = (packageId: string) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== packageId))
  }

  const updateQuantity = (packageId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(packageId)
      return
    }
    setCartItems((prevItems) =>
      prevItems.map((item) => (item.id === packageId ? { ...item, quantity } : item))
    )
  }

  const clearCart = () => {
    setCartItems([])
  }

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0)
  }

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0)
  }

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalPrice,
        getTotalItems
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

